import {useCallback} from 'react';
import PropTypes from 'prop-types';
import MixpanelUtils from '@livongo/utils/mixpanel';
import {useTranslation} from 'react-i18next';
import Arg from '@livongo/arg';
import APIUtils from '@livongo/utils/api';
import Button from 'ui/Button';
import Image from 'ui/Image';
import EnvironmentUtils from '@livongo/utils/environment';
import StorageUtils from '@livongo/utils/storage';
import ButtonReturn from '../navigation/ButtonReturn';
import {ACCESS_TOKEN_COOKIE} from '../config';

const Outro = ({
    isOneApp,
    userId,
    publicId,
    lessonId,
    supportLinks,
    onGoToLanding,
}) => {
    const {t} = useTranslation();

    const getAccessCode = token => {
        return APIUtils.post(
            'v1/users/me/auth/oAuth2CodeFromJWT',
            {
                jwt: token,
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        ).then(({data}) => data?.code ?? '');
    };

    const onConnectClick = useCallback(async () => {
        // eslint-disable-next-line node/no-unsupported-features/node-builtins, node/prefer-global/url
        const {origin, search} = new URL(supportLinks.getLivongoCoachUrl());

        const accessToken = StorageUtils.get({
            key: ACCESS_TOKEN_COOKIE,
            type: 'cookie',
        });

        const accessCode = await getAccessCode(accessToken);
        const accessCodeString = accessCode
            ? `&accesscode=${accessCode}&web=true`
            : '';
        let oneAppParams;
        const enableNewUi = '&enableNewUi=true';

        // Only OneApp passes these parameters in the URL
        if (isOneApp) {
            oneAppParams = `&${Arg('returnSubdomain')}&${Arg('returnPath')}`;
        }

        MixpanelUtils.track({
            event: 'ReMonj - Coach Card Clicked',
            properties: {
                'member ID': userId,
                timestamp: new Date().toISOString(),
                'lesson ID': lessonId,
            },
        });

        if (origin.startsWith('https://coaching')) {
            window.location.href = `${origin}/${search}&u=${publicId}${accessCodeString}${
                isOneApp ? `&ccmOneApp=true${oneAppParams}` : ''
            }${enableNewUi}`;
        } else {
            window.location.href = `${origin}/${publicId}${search}${accessCodeString}${
                isOneApp ? `&oneapp=true${oneAppParams}` : ''
            }${enableNewUi}`;
        }
    }, [userId, publicId, lessonId, supportLinks, isOneApp]);
    const title = t('outro.title');

    return (
        <div className="Outro">
            <div className="Outro-container">
                <div className="Outro-completion">
                    <Image src="../img/completed-outro.png" alt="" />
                    <h1 className="heading-md">{title}</h1>
                </div>
                <div className="Outro-actions">
                    <div className="Outro-action">
                        <div className="Outro-action-image Image-aspect-container">
                            <Image src="../img/weigh-in-outro.png" alt="" />
                        </div>
                        <div className="Outro-action-copy">
                            <h2 className="heading-sm">
                                {t('outro.one.heading')}
                            </h2>
                            <p>{t('outro.one.description')}</p>
                        </div>
                    </div>
                    <div className="Outro-action">
                        <div className="Outro-action-image Image-aspect-container Outro-coach-image">
                            <Image
                                src="../img/schedule-session-outro.png"
                                alt=""
                            />
                        </div>
                        <div className="Outro-action-copy">
                            <h2 className="heading-sm">
                                {t('outro.two.heading')}
                            </h2>
                            <p>{t('outro.two.description')}</p>

                            <Button
                                color="primary"
                                onClick={onConnectClick}
                                className="Outro-action-button"
                            >
                                {t('outro.two.cta')}
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="Outro-button">
                    <Button color="secondary" onClick={onGoToLanding}>
                        {t('outro.back')}
                    </Button>
                    {!EnvironmentUtils.isMobileDevice() && (
                        <ButtonReturn isOneApp={isOneApp} />
                    )}
                </div>
            </div>
        </div>
    );
};

Outro.propTypes = {
    isOneApp: PropTypes.bool,
    userId: PropTypes.string.isRequired,
    publicId: PropTypes.string.isRequired,
    lessonId: PropTypes.number.isRequired,
    supportLinks: PropTypes.object.isRequired,
    onGoToLanding: PropTypes.func.isRequired,
};

export default Outro;
